import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Box, Button, Divider, FormHelperText, Stack, Typography, } from '@mui/material';
import { PasswordValidation, MIN_PASSWORD_LENGTH } from 'components/kratos/ui';
import { useTranslation } from 'react-i18next';
import { getLayoutItems } from './items';
import { LoadingWrapper, TextFormItem } from 'components/custom';
import { Mail } from '@mui/icons-material';
import { useProviderQuery } from 'api';
import * as yup from 'yup';
const SPACING = 2.5;
/**
 * Mapping of Kratos UI node name to key.
 * Keeps away Kratos naming from the template code.
 */
const nameToKeyMap = {
    method: 'submit',
    password: 'password',
    provider: 'provider',
    'traits.accepted_gtc': 'gtc',
    'traits.accepted_tos': 'tos',
    'traits.email': 'email',
    'traits.name.first': 'firstName',
    'traits.name.last': 'lastName',
};
export const RegistrationTemplate = ({ children, setValidity, context, resetFormValues, }) => {
    const { t } = useTranslation('auth');
    const { t: tCommon } = useTranslation('common');
    const { customerType, setCustomerType } = context ?? {};
    const items = getLayoutItems(children, nameToKeyMap);
    const [errorMessage, setErrorMessage] = React.useState();
    const [providerQuery, providerQueryStatus] = useProviderQuery();
    const schema = React.useMemo(() => yup.string().email().label(t('body.flow.emailField.label')).required(), [t]);
    const onProviderQuerySuccess = (res) => {
        if (typeof res !== 'undefined') {
            setCustomerType?.(res.is_school ? 'business' : 'individual');
        }
    };
    const onNextClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const emailValue = items.email?.props?.value ?? '';
        const isValid = await schema
            .validate(emailValue)
            .catch((err) => {
            setErrorMessage(err.message);
        });
        if (!isValid) {
            return;
        }
        setErrorMessage('');
        providerQuery({ email: emailValue }, onProviderQuerySuccess);
    };
    const onClearEmail = () => {
        setCustomerType?.(undefined);
        resetFormValues();
    };
    return (_jsx(LoadingWrapper, { status: providerQueryStatus, children: _jsxs(Stack, { spacing: SPACING, children: [items.messages, _jsxs(Box, { hidden: !!customerType, children: [items.email, _jsx(FormHelperText, { error: !!errorMessage, children: errorMessage })] }), !customerType && (_jsx(_Fragment, { children: _jsx(Button, { color: "secondary", variant: "contained", type: "submit", onClick: (e) => {
                            void onNextClick(e);
                        }, children: tCommon('body.button.next') }) })), !!customerType && (_jsxs(_Fragment, { children: [items.providerList.length > 0 && (_jsxs(Stack, { gap: "2rem", children: [items.providerList, _jsx(Divider, { sx: { ':before, :after': { borderColor: 'black' } }, children: t('body.flow.dividerText') })] })), _jsx(TextFormItem, { label: t('body.flow.emailField.label'), StartAdornmentIcon: Mail, value: items.email?.props?.value, InputProps: {
                                readOnly: true,
                            }, clearButtonAriaLabel: t('body.flow.emailField.clearEmail'), onClear: onClearEmail }), _jsxs(Stack, { direction: "row", spacing: 3, children: [items.firstName, items.lastName] }), items.tos, items.gtc, _jsx(Divider, {}), _jsx(Typography, { variant: "h5", color: "secondary.main", children: t('body.step.registration.heading2') }), _jsx(Typography, { children: t('body.step.registration.paragraph1') }), _jsx(Typography, { sx: { fontWeight: 700 }, children: t('body.step.registration.paragraph2', {
                                minLength: MIN_PASSWORD_LENGTH,
                            }) }), _jsxs(Box, { children: [items.password, _jsx(PasswordValidation, { setValidity: setValidity, passwordValue: items.password?.props?.value, spacing: SPACING })] }), items.rest, items.submit] }))] }) }));
};
